<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin, deviceMixin } from "@/mixins";
import { domainService } from "@/services/api.service";
import { API_ENDPOINTS, PAGE_OPTIONS } from "@/constants";
import { requireAuth } from "@/utils/auth";
import { apiGet, apiPost } from "@/utils/request";

/**
 * DNS域名列表页面
 */
export default {
  page() {
    return {
      title: this.$t("menuitems.dns.list"),
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  mixins: [authMixin, deviceMixin],
  data() {
    return {
      title: "",
      items: [],
      activeNames: [],
      activeapp: [],
      loading: true,
      totalRows: 0,
      domainlist: [],
      // 添加域名对话框
      addDomainDialogVisible: false,
      addDomainForm: {
        domain: '',
      },
      addDomainRules: {
        domain: [
          { required: true, message: '请输入域名', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/, 
            message: '请输入有效的域名格式（例如：example.com）', 
            trigger: 'blur' }
        ],
      },
      addDomainLoading: false,
      addDomainFormRef: null,
    };
  },
  mounted() {
    this.initPage();
    this.getdomainlist();
  },
  methods: {
    isDomainEnabled(enable) {
      if (typeof enable === "boolean") return enable;
      if (typeof enable === "string") {
        return enable.toLowerCase() === "true" || enable === "1";
      }
      return enable === 1;
    },
    isDomainDisabled(enable) {
      return !this.isDomainEnabled(enable);
    },
    /**
     * 初始化页面
     */
    initPage() {
      this.title = this.$t("menuitems.dns.list");
      this.items = [
        {
          text: this.$t("menuitems.dns.text"),
          href: "/",
        },
        {
          text: this.$t("menuitems.dns.list"),
          active: true,
        },
      ];
    },

    /**
     * 获取域名列表
     */
    async getdomainlist() {
      if (!requireAuth(this)) {
        return;
      }
      
      try {
        const response = await apiGet(API_ENDPOINTS.DOMAINS, {}, { 
          vm: this, 
          showLoading: true, 
          loadingText: '加载域名列表...' 
        });
        
        if (response && response.status === 200) {
          this.loading = false;
          const domains = Array.isArray(response.data) ? response.data : [];
          this.domainlist = domains.sort(this.sortByDate);
          this.totalRows = this.domainlist.length;
        }
      } catch (error) {
        console.error('加载域名列表失败:', error);
        // 401错误已在adapter.js中处理，这里不需要额外操作
        if (error.response && error.response.status !== 401) {
          // 其他错误已在handleApiResponse中处理
        }
      }
    },
    torecords(domain) {
      this.$router.push({
        path: "/dns/" + domain + "/record",
      });
    },
    /**
     * 打开添加域名对话框
     */
    adddomain() {
      this.addDomainDialogVisible = true;
      this.addDomainForm.domain = '';
      // 重置表单验证状态
      this.$nextTick(() => {
        if (this.$refs.addDomainFormRef) {
          this.$refs.addDomainFormRef.clearValidate();
        }
      });
    },
    
    /**
     * 关闭添加域名对话框
     */
    closeAddDomainDialog() {
      this.addDomainDialogVisible = false;
      this.addDomainForm.domain = '';
      this.addDomainLoading = false;
      this.$nextTick(() => {
        if (this.$refs.addDomainFormRef) {
          this.$refs.addDomainFormRef.clearValidate();
        }
      });
    },
    
    /**
     * 验证域名格式
     */
    validateDomainFormat(domain) {
      if (!domain) {
        return { valid: false, message: '请输入域名' };
      }
      
      // 基本格式验证
      const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(domain)) {
        return { valid: false, message: '域名格式不正确（例如：example.com）' };
      }
      
      // 长度验证
      if (domain.length > 253) {
        return { valid: false, message: '域名长度不能超过253个字符' };
      }
      
      // 标签长度验证
      const labels = domain.split('.');
      for (const label of labels) {
        if (label.length > 63) {
          return { valid: false, message: '域名标签长度不能超过63个字符' };
        }
      }
      
      return { valid: true, message: '' };
    },
    
    /**
     * 提交添加域名表单
     */
    async submitAddDomain() {
      // 验证表单
      if (this.$refs.addDomainFormRef) {
        const valid = await this.$refs.addDomainFormRef.validate().catch(() => false);
        if (!valid) {
          return;
        }
      }
      
      const domain = this.addDomainForm.domain.trim().toLowerCase();
      
      // 再次验证域名格式
      const validation = this.validateDomainFormat(domain);
      if (!validation.valid) {
        this.$message({
          message: validation.message,
          type: 'error',
        });
        return;
      }
      
      this.addDomainLoading = true;
      
      try {
        await apiPost(
          API_ENDPOINTS.ADD_DOMAIN,
          { domain: domain },
          {},
          { 
            vm: this, 
            showSuccess: true, 
            successMessage: this.$t("dns.domain.add.success") || '域名添加成功',
            showLoading: false 
          }
        );
        
        // 添加成功，关闭对话框并刷新列表
        this.closeAddDomainDialog();
        await this.getdomainlist();
        
        // 可选：提示用户是否跳转到域名记录页面
        this.$confirm(
          this.$t("dns.domain.add.success") + '，是否立即配置DNS记录？',
          '添加成功',
          {
            confirmButtonText: '前往配置',
            cancelButtonText: '稍后配置',
            type: 'success',
          }
        ).then(() => {
          // 跳转到域名记录页面
          this.torecords(domain);
        }).catch(() => {
          // 用户选择稍后配置，不做任何操作
        });
      } catch (error) {
        // handleApiResponse 已经处理了基本错误消息
        if (error.response && error.response.data) {
          const status = error.response.data.status;
          if (status === 402) {
            this.$message({
              message: this.$t("dns.domain.exit") || '域名已存在',
              type: "error",
            });
          } else if (status === 300) {
            this.$message({
              message: this.$t("dns.domain.format") || '域名格式不正确或TLD不支持',
              type: "error",
            });
          }
        }
      } finally {
        this.addDomainLoading = false;
      }
    },
    /**
     * 删除域名
     */
    async deletedomain(domain_id, smscode = null) {
      if (!smscode) {
        // 首次确认删除
        try {
          await this.$confirm(
            this.$t("waring.tips.deletedomain"),
            this.$t("waring.tips.text"),
            {
              confirmButtonText: this.$t("alter.button.ok"),
              cancelButtonText: this.$t("alter.button.cancel"),
              type: "warning",
            }
          );
          
          // 执行删除请求（不带验证码）
          await this.executeDelete(domain_id);
        } catch (error) {
          // 用户取消操作
          if (error !== 'cancel') {
            console.error('删除域名失败:', error);
          }
        }
      } else {
        // 带验证码删除
        await this.executeDelete(domain_id, smscode);
      }
    },
    
    /**
     * 执行删除操作
     */
    async executeDelete(domain_id, smscode = null) {
      try {
        const data = { id: domain_id };
        if (smscode) {
          // 加密验证码
          data.smscode = this.$utils.crypto.encryptv2(smscode, this.$constants.AES_KEY);
        }
        
        const response = await apiPost(
          API_ENDPOINTS.DELETE_DOMAIN,
          data,
          {},
          { 
            vm: this, 
            showSuccess: true, 
            successMessage: this.$t("global.tips.alter.delete.success"),
            showLoading: true 
          }
        );
        
        if (response && response.status === 200) {
          this.getdomainlist();
        } else if (response && response.status === 403) {
          // 需要二次验证
          this.$message({
            message: this.$t("global.tips.alter.auth.needauth"),
            type: "warning",
            onClose: async () => {
              try {
                const { value } = await this.$prompt(
                  this.$t("global.tips.alter.auth.inputsms"),
                  this.$t("global.tips.alter.auth.needauth"),
                  {
                    confirmButtonText: this.$t("alter.button.ok"),
                    cancelButtonText: this.$t("alter.button.cancel"),
                  }
                );
                
                if (value && value.length === 6) {
                  this.deletedomain(domain_id, value);
                } else {
                  this.$message({
                    message: this.$t("global.sms.format"),
                    type: "warning",
                  });
                }
              } catch (error) {
                if (error !== 'cancel') {
                  console.error('输入验证码失败:', error);
                }
              }
            },
          });
        } else if (response && response.status === 408) {
          this.$message({
            message: this.$t("global.sms.busy"),
            type: "warning",
          });
        } else if (response && response.status === 405) {
          this.$message.error(this.$t("error.sms.code"));
        }
      } catch (error) {
        // handleApiResponse 已经处理了基本错误消息
        console.error('执行删除操作失败:', error);
      }
    },
    /**
     * 域名排序方法
     */
    sortByDomain(a, b) {
      const domainA = (a.domain || '').toLowerCase();
      const domainB = (b.domain || '').toLowerCase();
      if (domainA < domainB) return -1;
      if (domainA > domainB) return 1;
      return 0;
    },
    
    /**
     * 日期排序方法
     */
    sortByDate(a, b) {
      const dateA = a.last ? new Date(a.last).getTime() : 0;
      const dateB = b.last ? new Date(b.last).getTime() : 0;
      return dateB - dateA; // 降序排列
    },
    
    /**
     * 暂停/恢复域名
     */
    async suspend(domain_id, status) {
      const msg = status ? this.$t("waring.tips.recovery") : this.$t("waring.tips.suspend");
      const successMsg = status 
        ? this.$t("global.tips.alter.recovery.success") 
        : this.$t("global.tips.alter.suspend.success");
      
      try {
        await this.$confirm(
          msg,
          this.$t("waring.tips.text"),
          {
            confirmButtonText: this.$t("alter.button.ok"),
            cancelButtonText: this.$t("alter.button.cancel"),
            type: "warning",
          }
        );
        
        try {
          await apiPost(
            API_ENDPOINTS.SUSPEND_DOMAIN,
            { id: domain_id },
            {},
            { 
              vm: this, 
              showSuccess: true, 
              successMessage: successMsg,
              showLoading: true 
            }
          );
          this.getdomainlist();
        } catch (error) {
          // handleApiResponse 已经处理了错误消息
          console.error('暂停/恢复域名失败:', error);
        }
      } catch (error) {
        // 用户取消操作
        if (error !== 'cancel') {
          console.error('操作失败:', error);
        }
      }
    },
  },
  filters: {
    formatTimer(value) {
      if (!value) return '';
      const { formatDateTime } = require('@/utils/format');
      return formatDateTime(value);
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card domain-list-card">
          <div class="card-body">
            <div class="domain-toolbar">
              <div class="domain-toolbar-left">
                <h5 class="domain-toolbar-title mb-1">{{ $t("menuitems.dns.list") }}</h5>
                <span class="text-muted">
                  {{ (totalRows || domainlist.length || 0) }} {{ $t("dns.list.domain") }}
                </span>
              </div>
              <el-button type="primary" plain icon="el-icon-plus" @click="adddomain">{{
                $t("dns.domain.addnew")
              }}</el-button>
            </div>
            
            <!-- 添加域名对话框 -->
            <el-dialog
              :title="$t('dns.domain.addnew') || '添加域名'"
              :visible.sync="addDomainDialogVisible"
              width="600px"
              :close-on-click-modal="false"
              :close-on-press-escape="false"
              @close="closeAddDomainDialog"
            >
              <el-form
                ref="addDomainFormRef"
                :model="addDomainForm"
                :rules="addDomainRules"
                label-width="100px"
                label-position="left"
              >
                <el-form-item 
                  :label="$t('dns.list.domain') || '域名'" 
                  prop="domain"
                >
                  <el-input
                    v-model="addDomainForm.domain"
                    :placeholder="$t('waring.tips.input.domain') || '请输入域名，例如：example.com'"
                    clearable
                    @blur="validateDomainFormat(addDomainForm.domain)"
                  >
                    <template slot="prepend">
                      <i class="el-icon-link"></i>
                    </template>
                  </el-input>
                  <div style="margin-top: 8px; color: #909399; font-size: 12px;">
                    <p>• 请输入完整的域名，例如：example.com</p>
                    <p>• 域名将自动转换为小写字母</p>
                    <p>• 添加后将自动创建SOA和NS记录</p>
                  </div>
                </el-form-item>
                
                <!-- 域名预览信息 -->
                <el-form-item v-if="addDomainForm.domain && validateDomainFormat(addDomainForm.domain).valid">
                  <el-alert
                    :title="`域名预览：${addDomainForm.domain.trim().toLowerCase()}`"
                    type="info"
                    :closable="false"
                    show-icon
                  >
                  </el-alert>
                </el-form-item>
              </el-form>
              
              <div slot="footer" class="dialog-footer">
                <el-button @click="closeAddDomainDialog" :disabled="addDomainLoading">
                  {{ $t("global.button.cancel") || '取消' }}
                </el-button>
                <el-button 
                  type="primary" 
                  @click="submitAddDomain"
                  :loading="addDomainLoading"
                  icon="el-icon-check"
                >
                  {{ $t("global.button.ok") || '确定添加' }}
                </el-button>
              </div>
            </el-dialog>
            <!-- Element UI 表格 -->
            <div class="domain-table-wrapper">
              <el-table
                :data="domainlist"
                stripe
                border
                style="width: 100%"
                :fit="true"
                :empty-text="$t('dns.list.no_data') || '暂无数据'"
                tooltip-effect="dark"
                highlight-current-row
                :default-sort="{prop: 'last', order: 'descending'}"
                v-loading="loading"
                element-loading-text="加载中..."
              >
              <!-- 域名列 -->
              <el-table-column
                :label="$t('dns.list.domain')"
                prop="domain"
                min-width="200"
                show-overflow-tooltip
                sortable
                :sort-method="sortByDomain"
              >
                <template slot-scope="scope">
                  <el-link type="primary" @click="torecords(scope.row.domain)">
                    {{ scope.row.domain }}
                  </el-link>
                </template>
              </el-table-column>
              
              <!-- 记录数量列 -->
              <el-table-column
                :label="$t('dns.list.count')"
                prop="count"
                width="100"
                align="center"
                sortable
              >
              </el-table-column>
              
              <!-- 状态列 -->
              <el-table-column
                :label="$t('dns.list.status')"
                prop="status"
                width="120"
                align="center"
              >
                <template slot-scope="scope">
                  <el-tag
                    v-if="isDomainEnabled(scope.row.enable)"
                    type="success"
                    size="small"
                    effect="plain"
                  >
                    {{ $t("dns.domain.status.ok") }}
                  </el-tag>
                  <el-tag
                    v-else-if="isDomainDisabled(scope.row.enable)"
                    type="warning"
                    size="small"
                    effect="plain"
                  >
                    {{ $t("dns.domain.status.supu") }}
                  </el-tag>
                </template>
              </el-table-column>
              
              <!-- 最后修改时间列 -->
              <el-table-column
                :label="$t('dns.list.lastday')"
                prop="last"
                width="180"
                align="center"
                sortable
                :sort-method="sortByDate"
              >
                <template slot-scope="scope">
                  <span :title="scope.row.last | formatTimer">
                    {{ scope.row.last | formatTimer }}
                  </span>
                </template>
              </el-table-column>
              
              <!-- 操作列 -->
              <el-table-column
                :label="$t('dns.list.action')"
                width="320"
                align="center"
                fixed="right"
              >
                <template slot-scope="scope">
                  <el-button
                    type="primary"
                    size="small"
                    @click="torecords(scope.row.domain)"
                    plain
                  >
                    {{ $t("dns.domain.manguage") }}
                  </el-button>
                  <el-button
                    type="warning"
                    size="small"
                    @click="suspend(scope.row.id, false)"
                    plain
                    v-if="isDomainEnabled(scope.row.enable)"
                  >
                    {{ $t("dns.domain.status.supu") }}
                  </el-button>
                  <el-button
                    type="info"
                    size="small"
                    @click="suspend(scope.row.id, true)"
                    plain
                    v-if="isDomainDisabled(scope.row.enable)"
                  >
                    {{ $t("dns.domain.status.recovery") }}
                  </el-button>
                  <el-button
                    type="danger"
                    size="small"
                    @click="deletedomain(scope.row.id, 0)"
                    plain
                  >
                    {{ $t("dns.domain.delete") }}
                  </el-button>
                </template>
              </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* ==================== 页面内容区域优化 ==================== */
/* 移除过高的 min-height，让内容根据实际高度自适应 */
::v-deep .page-content {
  min-height: auto !important;
  padding-bottom: 20px;
}

::v-deep .card-body {
  padding: 20px;
}

.domain-list-card {
  border-radius: 10px;
}

.domain-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.domain-toolbar-title {
  font-size: 16px;
  font-weight: 600;
}

.domain-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

/* ==================== Element UI 表格样式 ==================== */
::v-deep .el-table {
  font-size: 14px;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  table-layout: auto;
  margin-top: 16px;
}

/* 表头样式 */
::v-deep .el-table th {
  background: linear-gradient(180deg, #f5f7fa 0%, #eef0f3 100%);
  font-weight: 600;
  color: #303133;
  padding: 12px 0;
  border-bottom: 2px solid #e4e7ed;
}

/* 表头单元格 */
::v-deep .el-table th > .cell {
  font-weight: 600;
  color: #303133;
}

/* 表格行样式 */
::v-deep .el-table td {
  padding: 12px 0;
}

/* 表格行 hover 效果 */
::v-deep .el-table tbody tr:hover > td {
  background-color: #f5f7fa;
}

/* 按钮组间距 */
::v-deep .el-table .cell .el-button + .el-button {
  margin-left: 5px;
}

/* 操作列按钮样式优化 */
::v-deep .el-table .el-button--small {
  padding: 7px 15px;
  transition: all 0.2s ease-in-out;
}

::v-deep .el-table .el-button--small:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 状态标签样式 */
::v-deep .el-table .el-tag {
  font-weight: 500;
}

/* 链接样式 */
::v-deep .el-table .el-link {
  font-weight: 500;
  text-decoration: none;
}

::v-deep .el-table .el-link:hover {
  text-decoration: underline;
}

/* 空状态样式 */
::v-deep .el-table__empty-block {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .el-table__empty-text {
  color: #909399;
  font-size: 14px;
}

/* 响应式优化 */
@media (max-width: 1200px) {
  ::v-deep .el-table {
    font-size: 13px;
  }
  
  ::v-deep .el-table th,
  ::v-deep .el-table td {
    padding: 10px 0;
  }
}

@media (max-width: 768px) {
  .domain-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .domain-toolbar-left {
    text-align: left;
  }

  ::v-deep .el-table {
    font-size: 12px;
  }
  
  ::v-deep .el-table .el-button--small {
    padding: 5px 10px;
    font-size: 12px;
  }
}

/* 高分辨率优化 */
@media (min-width: 1920px) {
  ::v-deep .el-table {
    font-size: 15px;
  }
  
  ::v-deep .el-table .el-table-column[data-column-key="domain"] {
    min-width: 250px !important;
  }
}
</style>